import React, { Fragment, useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  useSlsStateContext,
  getNestedSubelementsByType,
  getEventLabel,
  getMostRecentFieldValue,
  getImage,
  getNestedOrFloatingElementById,
} from '@fs/zion-sls'
import { FlowGrid, H1, Paragraph, Separator } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useWatch } from '@fs/zion-form'
import uniqBy from 'lodash.uniqby'
import EventForm from './EventForm'
import { containsRelationshipType } from '../utils/events.utils'

/**
 * EventFormsSection
 *
 * Renders a set of event form fields (either primary or additional). For each
 * event field it renders an autosuggest for the event type, optional remove
 * button (for non-primary events), and nested place/date sub-elements.
 *
 * @param {Object} props
 * @param {Array<Object>} props.fields - Field array items from `useFieldArray`, each representing an event.
 * @param {Object} props.fieldArrayMethods - Methods from `useFieldArray` used to manipulate the array (remove/append/etc.).
 * @param {Object} props.person - The person object associated with the events.
 * @param {string} [props.title] - Optional title to display above the first event form.
 * @param {boolean} [props.isPrimarySection=false] - Whether this is the primary event section (shows only primaryOnly events)
 * @param {string} [props.appName] - The application name for save operations
 * @param {string} [props.userId] - The user ID for save operations
 * @param {boolean} [props.saveCardDataToSls] - Whether to save card data to SLS
 * @param {Object} props.control - Form control from react-hook-form
 * @returns {JSX.Element} Rendered form section for events.
 */
const EventFormsSection = ({
  fields,
  fieldArrayMethods,
  title,
  person,
  appName,
  userId,
  saveCardDataToSls,
  control,
  isPrimarySection = false,
  splitScreenImageArk,
}) => {
  const [t] = useTranslation()
  const slsState = useSlsStateContext()
  const { types: slsTypes, images, currentImageArk } = slsState

  // Watch current form values to track already-added events
  const currentFormEventsWatch = useWatch({ control, name: 'events' })
  const currentFormEvents = useMemo(
    () => currentFormEventsWatch || { primary: [], additional: [] },
    [currentFormEventsWatch]
  )

  const selectData = useMemo(() => {
    // If no person prop, return only standard event types (filtered by primaryOnly)
    if (!person) {
      return slsTypes.events
        .filter((type) => {
          return isPrimarySection ? true : !type.primaryOnly
        })
        .map((type) => ({
          key: type.eventType,
          primaryText: type.displayLabel,
          isRelationshipEvent: containsRelationshipType(type),
          relType: type?.relType || null,
          group: t('edit-events.new-event', 'New Event'),
          isExistingEvent: false,
        }))
    }

    // Collect IDs of events already added to the form to prevent duplicates
    const alreadyAddedEventIds = new Set([
      ...(currentFormEvents?.primary || []).filter((e) => e.existingEventId).map((e) => e.existingEventId),
      ...(currentFormEvents?.additional || []).filter((e) => e.existingEventId).map((e) => e.existingEventId),
    ])

    // Helper function to get secondary text (date | place) for an event
    const getSecondaryText = (event) => {
      const dates = getNestedSubelementsByType(event, 'DATE', true)
      const places = getNestedSubelementsByType(event, 'PLACE', true)
      const dateFieldValue = dates.length > 0 ? getMostRecentFieldValue(dates[0]) : null
      const placeFieldValue = places.length > 0 ? getMostRecentFieldValue(places[0]) : null
      return [dateFieldValue, placeFieldValue].filter(Boolean).join(' | ')
    }

    // Get current record and image
    const currentRecord = person?.recordId ? getNestedOrFloatingElementById(images, person.recordId) : null
    const currentImage = getImage(images, currentImageArk)

    // Filter function to exclude events already linked to this person, couple events, already-added events,
    // and events that don't match the primaryOnly filter for this section
    const filterFunc = (ev) => {
      const attachedToThisPersonAlready = ev.superElements?.some((supEl) => supEl.id === person?.id)
      const isCoupleEvent = ev.typeData?.elementTypes?.includes('RELATIONSHIP')
      const alreadyAddedToForm = alreadyAddedEventIds.has(ev.id)
      // Filter by primaryOnly
      const isPrimaryOnlyEvent = ev.typeData?.primaryOnly
      const passesPrimaryFilter = isPrimarySection ? true : !isPrimaryOnlyEvent

      return !attachedToThisPersonAlready && !isCoupleEvent && !alreadyAddedToForm && passesPrimaryFilter
    }

    // Get existing events from this record
    const eventsInThisRecord = currentRecord
      ? getNestedSubelementsByType(currentRecord, 'EVENT')
          .filter(filterFunc)
          .map((event) => ({
            key: `existing_${event.id}`,
            primaryText: getEventLabel(event, slsTypes),
            secondaryText: getSecondaryText(event),
            group: t('edit-events.existing-events-in-record', 'Existing Events in This Record'),
            existingEvent: event, // Store the full event for later use
            isExistingEvent: true,
          }))
      : []

    // Get existing events from this image (excluding those already in record)
    const eventsInThisImage = currentImage
      ? getNestedSubelementsByType(currentImage, 'EVENT')
          .filter(filterFunc)
          .filter((ev) => !eventsInThisRecord.some((recordEv) => recordEv.existingEvent.id === ev.id))
          .map((event) => ({
            key: `existing_${event.id}`,
            primaryText: getEventLabel(event, slsTypes),
            secondaryText: getSecondaryText(event),
            group: t('edit-events.existing-events-in-image', 'Existing Events in This Image'),
            existingEvent: event,
            isExistingEvent: true,
          }))
      : []

    // Get floating persons events
    const floatingPersons = currentImage
      ? getNestedSubelementsByType(currentImage.floatingElements || [], 'PERSON')
      : []
    const eventsFromFloating = floatingPersons.flatMap((fp) =>
      getNestedSubelementsByType(fp, 'EVENT')
        .filter(filterFunc)
        .map((event) => ({
          key: `existing_${event.id}`,
          primaryText: getEventLabel(event, slsTypes),
          secondaryText: getSecondaryText(event),
          group: t('edit-events.existing-events-in-image', 'Existing Events in This Image'),
          existingEvent: event,
          isExistingEvent: true,
        }))
    )

    // Combine existing events and deduplicate
    const existingEvents = uniqBy([...eventsInThisRecord, ...eventsInThisImage, ...eventsFromFloating], (ev) => ev.key)

    // Standard event types with "New Event" group, filtered by primaryOnly
    // Allow CENSUS for all persons regardless of primaryOnly flag (RIP-4438)
    const newEventTypes = slsTypes.events
      .filter((type) => {
        return isPrimarySection ? true : !type.primaryOnly
      })
      .map((type) => ({
        key: type.eventType,
        primaryText: type.displayLabel,
        isRelationshipEvent: containsRelationshipType(type),
        relType: type?.relType || null,
        group: t('edit-events.new-event', 'New Event'),
        isExistingEvent: false,
      }))

    // Combine existing events first, then new event types
    return [...existingEvents, ...newEventTypes]
  }, [slsTypes, images, currentImageArk, person, t, currentFormEvents, isPrimarySection])

  if (fields.length === 0) return null

  return (
    <FlowGrid orientation="vertical" columnCount={1} gutters="xs">
      {fields.map((field, index) => (
        <Fragment key={field?.id || index}>
          {title && index === 0 && <H1 size="H6">{title}</H1>}
          {isPrimarySection && index === 0 && (
            <Paragraph size="sm">
              {t('eventsForm.primary-event-description', 'What is the main event captured in this document?')}
            </Paragraph>
          )}
          {index === 0 && <Separator size="xs" />}
          <EventForm
            field={field}
            index={index}
            fieldArrayMethods={fieldArrayMethods}
            eventAutosuggestOptions={selectData}
            person={person}
            appName={appName}
            userId={userId}
            saveCardDataToSls={saveCardDataToSls}
            splitScreenImageArk={splitScreenImageArk}
          />
        </Fragment>
      ))}
    </FlowGrid>
  )
}

EventFormsSection.propTypes = {
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      primary: PropTypes.bool,
      dates: PropTypes.arrayOf(PropTypes.shape({})),
      places: PropTypes.arrayOf(PropTypes.shape({})),
      other: PropTypes.arrayOf(PropTypes.shape({})),
    })
  ).isRequired,
  fieldArrayMethods: PropTypes.shape({
    remove: PropTypes.func,
    insert: PropTypes.func,
    append: PropTypes.func,
    prepend: PropTypes.func,
  }).isRequired,
  title: PropTypes.string,
  person: PropTypes.shape({
    id: PropTypes.string,
    recordId: PropTypes.string,
  }),
  isPrimarySection: PropTypes.bool,
  appName: PropTypes.string,
  userId: PropTypes.string,
  saveCardDataToSls: PropTypes.bool,
  control: PropTypes.shape({
    register: PropTypes.func,
    unregister: PropTypes.func,
    getFieldState: PropTypes.func,
    handleSubmit: PropTypes.func,
    reset: PropTypes.func,
    resetField: PropTypes.func,
    setError: PropTypes.func,
    clearErrors: PropTypes.func,
    setValue: PropTypes.func,
    setFocus: PropTypes.func,
    getValues: PropTypes.func,
    getFieldValue: PropTypes.func,
    trigger: PropTypes.func,
    formState: PropTypes.shape({
      isDirty: PropTypes.bool,
      isValid: PropTypes.bool,
      errors: PropTypes.shape({}),
    }),
    watch: PropTypes.func,
  }).isRequired,
}

export default EventFormsSection
